// src/components/relinq-score/CadastralCompletoPFValida.jsx
import React from "react";
import {
  HiOutlineUser,
  HiOutlineLocationMarker,
  HiOutlineMail,
  HiOutlinePhone,
  HiOutlineUsers,
  HiOutlineOfficeBuilding,
} from "react-icons/hi";

const Badge = ({ children, color = "emerald" }) => {
  const colors = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    red: "bg-red-50 text-red-700 border-red-200",
    yellow: "bg-amber-50 text-amber-700 border-amber-200",
    blue: "bg-sky-50 text-sky-700 border-sky-200",
    gray: "bg-slate-50 text-slate-700 border-slate-200",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${colors[color]}`}
    >
      {children}
    </span>
  );
};

const Pill = ({ children }) => (
  <span className="inline-flex items-center rounded-full bg-slate-50 px-3 py-1 text-[11px] text-slate-700 border border-slate-200">
    {children}
  </span>
);

const SectionCard = ({ icon: Icon, title, children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="mb-3 flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate-50 text-slate-600 border border-slate-200">
          <Icon className="h-4 w-4" />
        </div>
        <h3 className="text-sm font-semibold tracking-wide text-slate-700 uppercase">
          {title}
        </h3>
      </div>
    </div>
    <div className="space-y-3 text-sm text-slate-700">{children}</div>
  </div>
);

const formatCPF = (cpf) => {
  if (!cpf) return "-";
  const digits = cpf.replace(/\D/g, "");
  if (digits.length !== 11) return cpf;
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const [dd, mm, yyyy] = dateStr.split("/");
  if (!dd || !mm || !yyyy) return dateStr;
  return `${dd}/${mm}/${yyyy}`;
};

const sexToLabel = (sexo) => {
  if (sexo === "M") return "Masculino";
  if (sexo === "F") return "Feminino";
  return "-";
};

const firstAddress = (enderecos = []) => enderecos[0] || null;

const CadastralCompletoPFValida = ({ data }) => {
  const {
    statusRetorno,
    prioridade,
    dadosCadastrais = {},
    localizacao = {},
    qualificacao = {},
    providerId,
    provedorReserva,
  } = data || {};

  const enderecos = localizacao.enderecos || [];
  const telefones = localizacao.telefones || [];
  const emails = localizacao.emails || [];
  const pessoasRelacionadas = qualificacao.pessoasRelacionada || [];
  const households = qualificacao.houseHolds || [];
  const mainAddress = firstAddress(enderecos);

  const isObito =
    dadosCadastrais.obito &&
    String(dadosCadastrais.obito).toLowerCase() === "sim";

  return (
    <div className="w-full rounded-3xl border border-slate-200 bg-slate-50/60 p-4 text-slate-800 shadow-sm">
      {/* Header */}
      <div className="mb-5 flex gap-4 flex-row items-center justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sky-50 border border-sky-200">
            <HiOutlineUser className="h-5 w-5 text-sky-500" />
          </div>
          <div>
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <h2 className="text-base font-semibold text-slate-900 text-lg">
                {dadosCadastrais.nome || "Pessoa não identificada"}
              </h2>
              {dadosCadastrais.situacaoCPF && (
                <Badge
                  color={
                    dadosCadastrais.situacaoCPF === "Regular"
                      ? "emerald"
                      : "red"
                  }
                >
                  CPF {dadosCadastrais.situacaoCPF}
                </Badge>
              )}
              {isObito && <Badge color="red">Óbito informado</Badge>}
              {dadosCadastrais.pessoaPoliticamenteExposta && (
                <Badge color="yellow">PEP</Badge>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-600">
              <span>{formatCPF(dadosCadastrais.cpf)}</span>
              {dadosCadastrais.dataNascimento && (
                <>
                  <span className="h-1 w-1 rounded-full bg-slate-400" />
                  <span>
                    Nasc.: {formatDate(dadosCadastrais.dataNascimento)}
                  </span>
                </>
              )}
              {dadosCadastrais.signo && (
                <>
                  <span className="h-1 w-1 rounded-full bg-slate-400" />
                  <span>Signo: {dadosCadastrais.signo}</span>
                </>
              )}
              {dadosCadastrais.sexo && (
                <>
                  <span className="h-1 w-1 rounded-full bg-slate-400" />
                  <span>{sexToLabel(dadosCadastrais.sexo)}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Grid principal */}
      <div className="grid gap-4 grid-cols-2">
        {/* Dados Cadastrais */}
        <SectionCard icon={HiOutlineUser} title="Dados cadastrais">
          <div className="grid grid-cols-2 gap-3 text-[12px]">
            <div>
              <p className="text-slate-500">Nome</p>
              <p className="font-medium text-slate-900 text-[13px] break-words">
                {dadosCadastrais.nome || "-"}
              </p>
            </div>
            <div>
              <p className="text-slate-500">Nome da mãe</p>
              <p className="font-medium text-slate-900 text-[13px] break-words">
                {dadosCadastrais.nomeMae || "-"}
              </p>
            </div>
            <div>
              <p className="text-slate-500">Situação CPF</p>
              <p className="font-medium text-slate-800">
                {dadosCadastrais.situacaoCPF || "-"}
              </p>
            </div>
            <div>
              <p className="text-slate-500">Atualização CPF</p>
              <p className="font-medium text-slate-800">
                {formatDate(dadosCadastrais.dataAtualizacaoCPF)}
              </p>
            </div>
            <div>
              <p className="text-slate-500">Região de origem</p>
              <p className="font-medium text-slate-800">
                {dadosCadastrais.regiaoOrigemCPF || "-"}
              </p>
            </div>
            <div>
              <p className="text-slate-500">Óbito</p>
              <p
                className={`font-medium ${
                  isObito ? "text-red-600" : "text-emerald-600"
                }`}
              >
                {dadosCadastrais.obito || "-"}
              </p>
            </div>
          </div>
        </SectionCard>

        {/* Localização / Endereço principal */}
        <SectionCard icon={HiOutlineLocationMarker} title="Localização">
          {mainAddress ? (
            <>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-[12px]">
                <p className="font-medium text-slate-900 text-[12px]">
                  {mainAddress.tipoLogradouro
                    ? `${mainAddress.tipoLogradouro}. `
                    : ""}
                  {mainAddress.logradouro}{" "}
                  {mainAddress.numero && `, ${mainAddress.numero}`}
                </p>
                <p className="text-slate-700">
                  {mainAddress.bairro && `${mainAddress.bairro} - `}
                  {mainAddress.cidade}/{mainAddress.uf}
                </p>
                <p className="text-slate-500">
                  CEP: {mainAddress.cep || "-"}
                  {mainAddress.complemento
                    ? ` • ${mainAddress.complemento}`
                    : ""}
                </p>
              </div>
              {enderecos.length > 1 && (
                <div className="space-y-1 text-[11px]">
                  <p className="text-[10px] uppercase tracking-wide text-slate-500">
                    Outros endereços ({enderecos.length - 1})
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {enderecos.slice(1).map((e, idx) => (
                      <Pill key={idx}>
                        {e.logradouro}, {e.numero || "s/n"} - {e.bairro} •{" "}
                        {e.cidade}/{e.uf}
                      </Pill>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-[11px] text-slate-500">
              Nenhum endereço encontrado.
            </p>
          )}
        </SectionCard>
      </div>

      {/* Grid secundário */}
      <div className="mt-4 grid gap-4 grid-cols-2">
        {/* Contatos */}
        <SectionCard icon={HiOutlinePhone} title="Contatos">
          <div className="space-y-2">
            <div>
              <p className="mb-1 text-[10px] uppercase tracking-wide text-slate-500">
                Telefones ({telefones.length})
              </p>
              {telefones.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {telefones.map((tel, idx) => (
                    <Pill key={idx}>
                      ({tel.ddd}) {tel.numero}{" "}
                      {tel.tipo && (
                        <span className="ml-1 text-[10px] text-slate-500">
                          • {tel.tipo}
                        </span>
                      )}
                    </Pill>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-slate-500">Nenhum telefone.</p>
              )}
            </div>

            <div>
              <p className="mb-1 mt-2 flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-500">
                <HiOutlineMail className="h-3 w-3" />
                E-mails ({emails.length})
              </p>
              {emails.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {emails.map((email, idx) => (
                    <Pill key={idx}>{email}</Pill>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-slate-500">Nenhum e-mail.</p>
              )}
            </div>
          </div>
        </SectionCard>
        {/* Pessoas relacionadas */}
        <SectionCard icon={HiOutlineUsers} title="Pessoas relacionadas">
          {pessoasRelacionadas.length ? (
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
              <table className="min-w-full text-left text-[12px]">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="px-3 py-2 font-normal">Nome</th>
                    <th className="px-3 py-2 font-normal w-24">Grau</th>
                    <th className="px-3 py-2 font-normal w-40">CPF</th>
                  </tr>
                </thead>
                <tbody>
                  {pessoasRelacionadas.map((p, idx) => (
                    <tr
                      key={idx}
                      className="border-t border-slate-100 hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-3 py-2 text-slate-900">{p.nome}</td>
                      <td className="px-3 py-2 text-slate-700">
                        {p.grauParentesco || "-"}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {p.cpf ? formatCPF(p.cpf) : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-[12px] text-slate-500">
              Nenhuma pessoa relacionada encontrada.
            </p>
          )}
        </SectionCard>
      </div>

      <div className="mt-4">
        {/* Households */}
        <SectionCard
          icon={HiOutlineOfficeBuilding}
          title="Household / Coabitantes"
        >
          {households.length ? (
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
              <table className="min-w-full text-left text-[13px]">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="px-3 py-2 font-normal">Nome</th>
                    <th className="px-3 py-2 font-normal w-16">Idade</th>
                    <th className="px-3 py-2 font-normal w-32">Nasc.</th>
                    <th className="px-3 py-2 font-normal w-20">Signo</th>
                    <th className="px-3 py-2 font-normal w-8">Sexo</th>
                  </tr>
                </thead>
                <tbody>
                  {households.map((h, idx) => (
                    <tr
                      key={idx}
                      className="border-t border-slate-100 hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-3 py-2 text-slate-900">{h.nome}</td>
                      <td className="px-3 py-2 text-slate-700">
                        {h.idade || "-"}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {formatDate(h.dataNascimento)}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {h.signo || "-"}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {sexToLabel(h.sexo || "").charAt(0) || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-[11px] text-slate-500">
              Nenhum household encontrado.
            </p>
          )}
        </SectionCard>
      </div>
    </div>
  );
};

export default CadastralCompletoPFValida;
